import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faCardsBlank,
  faCheck,
  faShieldAlt,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { defineMessages } from 'react-intl';
import styled from 'styled-components';

import { CardRequirements } from '../CardRequirements';
import { Participants } from '../Participants';
import { Row } from './Row';
import { CompetitionDetailsGameplayTable_LeaderboardInterface } from './__generated__/index.graphql';

const messages = defineMessages({
  participants: {
    id: 'CompetitionDetails.Gameplay.Participants.Label',
    defaultMessage: 'Participants',
  },
  cardRequirements: {
    id: 'CompetitionDetails.Gameplay.CardRequirements.Label',
    defaultMessage: 'Cards',
  },
  minCurrentSeasonCardCountRequirements: {
    id: 'CompetitionDetails.Gameplay.urrentSeasonCardCount.Label',
    defaultMessage: 'Current Season Cards',
  },
  budgetRequirements: {
    id: 'CompetitionDetails.Gameplay.BudgetRequirements.Label',
    defaultMessage: 'Budget',
  },
  scoringStrategy: {
    id: 'CompetitionDetails.Gameplay.ScoringStrategy.Label',
    defaultMessage: 'Scoring',
  },
});

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  grid-template-rows: max-content;
  width: 100%;
  --row-border-color: var(--c-neutral-300);
  border-bottom: 1px solid var(--row-border-color);
  & > div {
    border-top: 1px solid var(--row-border-color);
  }
  &.loading > div {
    color: var(--c-neutral-400);
  }
`;

type Props = {
  leaderboard?: CompetitionDetailsGameplayTable_LeaderboardInterface | null;
  nbCards: number;
  BudgetRequirements?: ReactNode;
  ScoringStrategy?: ReactNode;
};

export const Table = ({
  leaderboard,
  nbCards,
  BudgetRequirements,
  ScoringStrategy,
}: Props) => {
  return (
    <Wrapper className={classNames({ loading: !leaderboard })}>
      <Row label={messages.participants} icon={faUser}>
        <Participants leaderboard={leaderboard} />
      </Row>
      <Row label={messages.cardRequirements} icon={faCardsBlank}>
        <CardRequirements
          requirements={leaderboard?.requirements}
          nbCards={nbCards}
        />
      </Row>
      {BudgetRequirements && (
        <Row label={messages.budgetRequirements} icon={faShieldAlt}>
          {BudgetRequirements}
        </Row>
      )}
      {ScoringStrategy && (
        <Row label={messages.scoringStrategy} icon={faCheck}>
          {ScoringStrategy}
        </Row>
      )}
    </Wrapper>
  );
};

Table.fragments = {
  LeaderboardInterface: gql`
    fragment CompetitionDetailsGameplayTable_LeaderboardInterface on LeaderboardInterface {
      slug
      ...CompetitionDetailsGameplayParticipants_LeaderboardInterface
      requirements {
        ...CompetitionDetailsGameplayCardRequirements_LeaderboardRequirementsInterface
      }
    }
    ${Participants.fragments.LeaderboardInterface}
    ${CardRequirements.fragments.LeaderboardRequirementsInterface}
  ` as TypedDocumentNode<CompetitionDetailsGameplayTable_LeaderboardInterface>,
};
